import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _toArray from "@babel/runtime/helpers/toArray";
import _ from '@sparta.global.vendors/lodash';
import spartaEventsUtility from '@sparta.global.utilities/sparta-events-utility/dist/es5.js';
var EVENTS = {
  ELEMENT_UNSTICK: 'UNSTICK.spartaStickyUtility',
  ELEMENT_STICK: 'STICK.spartaStickyUtility',
  ELEMENT_FADE_IN: 'FADE_IN.spartaStickyUtility',
  SPARTA_GLOBAL_READY: 'SPARTA_GLOBAL_READY.spartaBootstrapUtility'
};
var DELAYS = {
  SHORT: 50,
  MID: 500,
  LONG: 1000
};
var KEYS = {
  HOME: 36
};
var CLASSES = {
  HIDDEN: 'hidden',
  TOUCH: 'touch',
  STICKY: 'spa-sticky',
  ACTIVE: 'active',
  FADE_IN: 'fade-in',
  PLACEHOLDER: 'sticky-placeholder',
  PLACEHOLDER_FADE: 'sticky-placeholder-fade'
};

// Cache the viewport width.
var viewportWidth = document.documentElement.clientWidth;

/**
 * Gets the total height of the page.
 * @returns {number} - The page height in pixels.
 */
function getPageHeight() {
  return Math.max(document.body.scrollHeight, document.body.offsetHeight, document.documentElement.clientHeight, document.documentElement.scrollHeight, document.documentElement.offsetHeight);
}

/**
 * Gets the current Y position of the screen.
 * @returns {number} - The current scroll position in pixels.
 */
function getCurrentDist() {
  return document.documentElement.scrollTop || document.body.scrollTop;
}

/**
 * IE-compatible `scrollTo` replacement.
 * @param {number} dist - Target document Y position.
 * @returns {void}
 */
function scrollToY(dist) {
  if (document.body.scrollTop > document.documentElement.scrollTop) {
    document.body.scrollTop = dist;
  } else {
    document.documentElement.scrollTop = dist;
  }
}

/**
 * Calculates the total height of all actively "stuck" sticky elements (excluding the passed element).
 * @param {HTMLElement} Element to exclude.
 * @returns {number} Sum of actively "stuck" element heights.
 */
function getLevelOffset(el) {
  var levelOffset = 0;
  var totalStickyActiveElment = document.querySelectorAll(".".concat(CLASSES.STICKY, ".").concat(CLASSES.ACTIVE));
  for (var i = totalStickyActiveElment.length - 1; i >= 0; i -= 1) {
    var levelEl = document.querySelector(".".concat(CLASSES.STICKY, ".").concat(CLASSES.ACTIVE));
    if (levelEl && levelEl !== el) {
      levelOffset += levelEl.offsetHeight;
    }
  }
  return levelOffset;
}

/**
 * Calculates the vertical position of a given DOM element.
 * @param {object} el - The DOM element to check.
 * @returns {number} - The vertical position in pixels, defaulting to zero.
 */
function getElOffset(el) {
  if (!el || !document.body.contains(el)) {
    return 0;
  }
  return Math.round(el.getBoundingClientRect().top + getCurrentDist());
}

// Add/remove sticky element styles
// eslint-disable-next-line require-jsdoc
function stickyElStyle(element, style) {
  var setStyle = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  if (style && typeof style === 'string') {
    style.replace(/ /g, '').split(';').filter(function (el) {
      return el !== '';
    }).forEach(function (arrEl) {
      var _arrEl$split = arrEl.split(':'),
        _arrEl$split2 = _toArray(_arrEl$split),
        prop = _arrEl$split2[0],
        val = _arrEl$split2.slice(1);
      val = val.join(':').trim();
      prop = prop.trim();
      if (setStyle) {
        // eslint-disable-next-line no-param-reassign
        element.style[prop] = val;
      } else {
        // eslint-disable-next-line no-param-reassign
        element.style[prop] = null;
      }
    });
  }
}

/**
 * Private helper function.
 * @param {object} el - A DOM reference to the sticky element.
 * @returns {object} `stickPoint` and `unstickPoint` Y values.
 */
function getStickPoints(el) {
  // Temporarily remove styling/classes to properly calculate the offset.
  var style = el.getAttribute('style');
  var isActive = el.classList.contains(CLASSES.ACTIVE);
  var isHidden = el.classList.contains(CLASSES.HIDDEN);
  var removeStyling = !(el.dataset.noPlaceholder || el.dataset.stickyFade);
  if (removeStyling) {
    stickyElStyle(el, style);
    if (isActive) {
      el.classList.remove(CLASSES.ACTIVE);
    }
    if (isHidden) {
      el.classList.remove(CLASSES.HIDDEN);
    }
  }
  var pageHeight = getPageHeight();
  var parentSel = el.dataset.stickyParent;
  var parentEl = parentSel && parentSel !== 'body' ? el.closest(parentSel) : null;
  var stickStartEl = el.dataset.stickyStart ? document.querySelector(el.dataset.stickyStart) : null;
  if (!stickStartEl) {
    if (isActive || isHidden) {
      stickStartEl = el.parentNode.querySelector(".".concat(CLASSES.PLACEHOLDER, ", .").concat(CLASSES.PLACEHOLDER_FADE));
    } else {
      stickStartEl = el;
    }
  }
  var stickPoint = getElOffset(stickStartEl);
  var stickEndEls = el.dataset.stickyEnd ? document.querySelectorAll(el.dataset.stickyEnd) : [];
  var stickEndEl = stickEndEls.length ? stickEndEls[stickEndEls.length - 1] : null;
  var unstickPoint = stickEndEl ? getElOffset(stickEndEl) - el.offsetHeight : pageHeight;

  // If there's a parent specified, use the bottom of that element as the unstick point.
  if (parentEl) {
    unstickPoint = getElOffset(parentEl) + parentEl.offsetHeight;
  }

  // If we're using a global footer, determine whether that should affect the sticky.
  var footer = document.querySelector('#globalFooterModule, #universalFooterModule');
  if (footer) {
    var stickyOverFooter = el.dataset.stickyFooter;
    if (!stickyOverFooter && !parentEl && !stickEndEl) {
      // Unstick at the global footer.
      unstickPoint = getElOffset(footer) - el.offsetHeight;
    }
  }

  // Add the temporarily removed styling/classes back.
  if (removeStyling) {
    stickyElStyle(el, style, true);
    if (isActive) {
      el.classList.add(CLASSES.ACTIVE);
    }
    if (isHidden) {
      el.classList.add(CLASSES.HIDDEN);
    }
  }
  return {
    stickPoint: stickPoint,
    unstickPoint: unstickPoint
  };
}

/**
 * Returns all keyboard-focusable elements within a specified element.
 * @param {object} [el] - The parent element to search through.
 * @returns {array} A set of all the focusable elements found.
 */
function getFocusableEls() {
  var el = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : document;
  return _toConsumableArray(el.querySelectorAll('a, button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])'));
}

/**
 * Scrolls to the main content when SkipToMainContent anchor tag is clicked
 * @returns {void}
 */
function skipToMainContent() {
  // Find the anchor tag with 'Skip to main content' link
  var skipLink = document.getElementById('ada-skip-link') || document.getElementById('lnkSkipToMainContent') || document.getElementById('skiptocontent') || document.querySelector('[href="#content"]');
  if (skipLink && !skipLink.dataset.spaSkipStickyListener) {
    skipLink.addEventListener('click', function (event) {
      event.preventDefault();
      var mainContent = document.getElementById(skipLink.href.split('#')[1]);
      if (mainContent) {
        var mainContentPosition = mainContent.getBoundingClientRect().top + window.scrollY;

        // calculate total height of sticky(s) above the main Content
        var stickyElments = _toConsumableArray(document.querySelectorAll('div.spa-sticky[data-sticky-parent="body"]'));
        var totalStickyHeight = 0;
        stickyElments.forEach(function (element) {
          var elementPosition = element.getBoundingClientRect().top + window.scrollY;

          // only consider sticky(s) above the main content
          if (elementPosition < mainContentPosition) {
            var height = 0;

            // calculate the height of hidden sticky(s) if any
            if (element.classList.contains(CLASSES.HIDDEN)) {
              element.classList.remove(CLASSES.HIDDEN);
              height = element.offsetHeight;
              element.classList.add(CLASSES.HIDDEN);
            } else {
              // calculate the height of sticky(s) which are not hidden
              height = element.offsetHeight;
            }
            totalStickyHeight += height;
          }
        });

        // scroll to main content
        var scrollPosition = mainContentPosition - totalStickyHeight;
        window.scrollTo({
          top: scrollPosition,
          behavior: 'smooth'
        });
      }
    });

    // set flag to indicate the click listener has been added
    skipLink.dataset.spaSkipStickyListener = 'true';
  }
}

/**
 * Class for sticky elements.
 */
var SpartaStickyEl = /*#__PURE__*/function () {
  /**
   * @param {object} el - The DOM element to make sticky.
   * @param {number} level - The intended sticky level for this element.
   * @param {number} stickPoint - How far down the page to start sticking.
   * @param {number} unstickPoint - How far down the page to stop showing the element.
   * @param {number} [gap] - An offset to be subtracted from `unstickPoint`.
   */
  function SpartaStickyEl(el, level, stickPoint, unstickPoint) {
    var gap = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : 150;
    _classCallCheck(this, SpartaStickyEl);
    this.el = el;
    this.level = level;
    this.levelOffset = getLevelOffset(this.el);
    this.stickPoint = stickPoint;
    this.unstickPoint = unstickPoint;
    this.gap = gap;
    this.isActive = false;
    this.fullWidth = el.dataset.stickyFullWidth;
    this.fade = el.dataset.stickyFade;
    this.noPlaceholder = el.dataset.noPlaceholder;
    this.addPlaceholder();
    if (this.fade) {
      this.el.classList.add(CLASSES.HIDDEN);
    }
  }

  /**
   * @returns {object} A collection of namespaced event strings for this utility.
   */
  return _createClass(SpartaStickyEl, [{
    key: "EVENTS",
    get: function get() {
      return EVENTS;
    }

    /**
     * Attaches a hidden placeholder element after the sticky element to prevent layout issues as the
     * element sticks and unsticks.
     * @returns {void}
     */
  }, {
    key: "addPlaceholder",
    value: function addPlaceholder() {
      var stickyPlaceholder = this.el && this.el.parentNode.querySelectorAll(".".concat(CLASSES.PLACEHOLDER, ", .").concat(CLASSES.PLACEHOLDER_FADE));
      if (stickyPlaceholder && stickyPlaceholder.length > 0) {
        // eslint-disable-next-line prefer-destructuring
        this.placeholder = stickyPlaceholder[0];
      } else {
        this.placeholder = document.createElement('div');
        this.placeholder.classList.add(this.fade ? CLASSES.PLACEHOLDER_FADE : CLASSES.PLACEHOLDER);
      }
      this.updatePlaceholder();
      this.el.insertAdjacentElement('beforebegin', this.placeholder);
    }

    /**
     * Updates the placeholder element size.
     * @returns {void}
     */
  }, {
    key: "updatePlaceholder",
    value: function updatePlaceholder() {
      if (this.noPlaceholder || this.fade) {
        return;
      }
      if (!this.isActive) {
        this.placeholder.style.height = '0';
        return;
      }
      var _getComputedStyle = getComputedStyle(this.el),
        marginTop = _getComputedStyle.marginTop,
        marginBottom = _getComputedStyle.marginBottom;
      var height = this.el.offsetHeight + parseInt(marginTop, 10) + parseInt(marginBottom, 10);
      this.placeholder.style.height = "".concat(height, "px");
    }

    /**
     * Removes the placeholder element. Called in conjunction with removing the sticky property of an
     * element.
     * @returns {void}
     */
  }, {
    key: "removePlaceholder",
    value: function removePlaceholder() {
      this.placeholder.parentNode.removeChild(this.placeholder);
    }

    /**
     * Resets the element's class/style and recalculates the stick point.
     * @param {number} dist - Current document Y position.
     * @returns {void}
     */
  }, {
    key: "handleResize",
    value: function handleResize(dist) {
      var _getStickPoints = getStickPoints(this.el),
        stickPoint = _getStickPoints.stickPoint,
        unstickPoint = _getStickPoints.unstickPoint;
      this.stickPoint = stickPoint;
      this.unstickPoint = unstickPoint;
      this.updateSticky(dist);
      this.updatePlaceholder();
      this.reArrangeStickyOrder();

      // Avoid any jumpiness caused by sticking/unsticking elements.
      if (dist !== getCurrentDist()) {
        scrollToY(dist);
      }
    }

    /**
     * Calculates the height of active sticky elements
     * and re-arrange the sticky element's style top value
     * @returns {void}
     * */
  }, {
    key: "reArrangeStickyOrder",
    value: function reArrangeStickyOrder() {
      var stickyActiveElment = document.querySelectorAll('.spa-sticky.active');
      var calculateStickyHeight = 0;
      stickyActiveElment = Array.from(stickyActiveElment).sort(function (a, b) {
        // eslint-disable-next-line no-param-reassign
        a = a.dataset.stickyLevel;
        // eslint-disable-next-line no-param-reassign
        b = b.dataset.stickyLevel;
        return a - b;
      });
      stickyActiveElment.forEach(function (_element, i) {
        stickyActiveElment[i].style.top = "".concat(calculateStickyHeight, "px");
        calculateStickyHeight += stickyActiveElment[i].offsetHeight;
      });
    }

    /**
     * Updates the attributes of the sticky element.
     * @param {number} dist - Current document Y position.
     * @returns {void}
     */
  }, {
    key: "updateSticky",
    value: function updateSticky(dist) {
      var _this = this;
      var stickyLevelOffset = getLevelOffset(this.el);
      var stickPoint = this.stickPoint - stickyLevelOffset;
      var unstickPoint = this.unstickPoint - stickyLevelOffset - this.gap;
      if (dist >= stickPoint && dist < unstickPoint) {
        // Inside the sticky zone...
        // Calculate positioning for non-hidden items.
        var width = '100%';
        var left = '0';
        if (!this.fullWidth && this.placeholder.offsetWidth) {
          width = "".concat(this.placeholder.offsetWidth, "px");
          left = "".concat(this.placeholder.getBoundingClientRect().left, "px");
        }
        this.el.style.width = width;
        this.el.style.left = left;

        // Update the element if it's newly sticky or if the offset has changed.
        if (!this.isActive || stickyLevelOffset !== this.levelOffset) {
          this.el.classList.add(CLASSES.ACTIVE);
          this.el.style.top = "".concat(stickyLevelOffset, "px");
          this.levelOffset = stickyLevelOffset;
          if (this.fade) {
            this.el.classList.remove(CLASSES.HIDDEN);
            this.el.classList.add(CLASSES.FADE_IN);

            // Publish a one-time Sparta event when the fade-in animation completes.
            var _fadeInHandler = function fadeInHandler(e) {
              if (e.animationName !== 'fadeIn' || !_this.el.isEqualNode(e.target)) {
                return;
              }
              spartaEventsUtility.pub(EVENTS.ELEMENT_FADE_IN, _this.el);
              _this.el.removeEventListener('animationend', _fadeInHandler);
            };
            this.el.addEventListener('animationend', _fadeInHandler, false);
          }

          // Publish a Sparta event when an element intially sticks.
          if (!this.isActive) {
            this.isActive = true;
            spartaEventsUtility.pub(EVENTS.ELEMENT_STICK, this.el);
          }
        }
      } else if (this.isActive) {
        // Leaving the sticky zone...
        if (dist <= stickPoint || dist > unstickPoint) {
          this.el.classList.remove(CLASSES.ACTIVE, CLASSES.FADE_IN);
          if (this.fade) {
            this.el.classList.add(CLASSES.HIDDEN);
          }

          // Remove the inline styling added while the sticky was active.
          this.el.style.width = null;
          this.el.style.left = null;
          this.el.style.top = null;
        }

        // Publish a Sparta event when an element intially unsticks.
        this.isActive = false;
        spartaEventsUtility.pub(EVENTS.ELEMENT_UNSTICK, this.el);
      }
    }
  }]);
}();
/**
 * `initializeStickyModules` scans the DOM and creates a new `SpartaStickyEl` for each
 * `.spa-sticky` element. It also manages the `scroll` and `resize` window events.
 * @param {object} els - A `nodeList` of `.spa-sticky` elements.
 */
var stickyApi = function initializeStickyModules(els) {
  var sticky = {
    /**
     * Caches values, sets up events, and creates instances.
     * @param {Boolean} force Forces full initialization sequence if no sticky elements are found.
     * @returns {void}
     */
    init: function init() {
      var force = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
      // If this sticky has already been initialized, ignore any subsequent attempts.
      if (this.initialized) {
        this.createStickyInstances(document.querySelectorAll(".".concat(CLASSES.STICKY, ":not([data-initialized])")));
        return;
      }

      // If there are no sticky elements already detected, query the DOM again.
      if (!els || !els.length) {
        // eslint-disable-next-line no-param-reassign
        els = document.querySelectorAll(".".concat(CLASSES.STICKY));
      }

      // If there are still no stickies on the page yet, exit early.
      // If force is true then keep going, this is needed for v-sticky directive
      if (!force && !els.length) {
        return;
      }
      this.isMobile = document.documentElement.classList.contains(CLASSES.TOUCH);
      this.stickyEls = _toConsumableArray(els);
      this.stickyElsInstances = [];
      this.eventSigs = [];
      this.setUpObservers();
      this.setUpEvents();
      this.createStickyInstances();
      this.checkBrowserFocus();
      this.pageHeight = getPageHeight();
      this.initialized = true;
    },
    /**
     * Sets the `scroll` event on `window`. No throttling here allows the sticky items to stick
     * with no jumping. Sets `orientationchange` or `resize` events depending on mobile status. The
     * `orientationchange` event is further delayed to allow for all media query CSS to be applied
     * before recalculating positions.
     * @returns {void}
     */
    setUpEvents: function setUpEvents() {
      var _this2 = this;
      // Save some specific event handler references in case we need to remove them later.
      this.boundResizeHandler = this.resizeHandler.bind(this);
      this.throttledResizeHandler = _.throttle(this.boundResizeHandler, DELAYS.LONG);
      this.delayedResizeHandler = _.throttle(function () {
        _.delay(_this2.boundResizeHandler, DELAYS.SHORT);
      }, DELAYS.MID);
      this.throttledHeightCheck = _.throttle(this.checkPageHeight.bind(this), DELAYS.SHORT);
      this.boundScrollHandler = this.updateStickyInstances.bind(this);
      this.throttledScrollHandler = _.throttle(this.boundScrollHandler, DELAYS.SHORT);

      // Trigger resize when modals close.
      spartaEventsUtility.sub('UNFREEZE_SCROLL.spartaUILayersUtility', _.throttle(function () {
        _.delay(_this2.boundResizeHandler, DELAYS.SHORT);
      }, DELAYS.MID));

      // Trigger resize when a sticky element unsticks.
      spartaEventsUtility.sub(EVENTS.ELEMENT_UNSTICK, this.boundResizeHandler);

      // Add the window event listeners.
      skipToMainContent();
      this.addEvent(window, 'scroll', this.throttledHeightCheck);
      if (this.isMobile) {
        this.addEvent(window, 'scroll', this.throttledScrollHandler);
        this.addEvent(window, 'orientationchange', this.delayedResizeHandler);
      } else {
        this.addEvent(window, 'scroll', this.boundScrollHandler);
        this.addEvent(window, 'touchmove', this.boundScrollHandler);
        this.addEvent(window, 'resize', this.throttledResizeHandler);

        // IE patch: When hitting the Home key, we need to remove the active state on all stickies,
        // since the scroll handler is never invoked compared to other browsers.
        document.addEventListener('keydown', function (e) {
          var key = e.which || e.keyCode || 0;
          if (key === KEYS.HOME) {
            _this2.removeActiveState();
          }
        }, false);
      }

      // Whenever a page element is focused, ensure that it's not covered up by a sticky.
      getFocusableEls().forEach(function (el) {
        _this2.addEvent(el, 'focus', function () {
          var stickyHeight = getLevelOffset(_this2.el);
          var focusPos = Math.round(el.getBoundingClientRect().top);
          var isInSticky = false;
          _this2.stickyEls.forEach(function (element) {
            isInSticky = isInSticky || element.contains(el);
          });
          if (stickyHeight > focusPos && !isInSticky && _this2.browserTabFocused) {
            scrollToY(getCurrentDist() - stickyHeight + focusPos);
          }
        });
      });
    },
    /**
     * Returns a boolean to see if browser tab is currently active.
     * Used to prevent page shift in chromium browsers.
     * @returns {void}
     */
    checkBrowserFocus: function checkBrowserFocus() {
      var _this3 = this;
      this.browserTabFocused = true;
      document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'hidden') {
          _this3.browserTabFocused = false;
        } else {
          // Re-enable boolean once browser tab status is active again.
          setTimeout(function () {
            _this3.browserTabFocused = true;
          }, 500);
        }
      });
    },
    /**
     * Adds a new event listener, keeping track of its signature.
     * @param {object} el - The DOM element to attach the event to.
     * @param {string} eventName - The name of the event to listen for.
     * @param {function} handler - The event handler to apply.
     * @returns {void}
     */
    addEvent: function addEvent(el, eventName, handler) {
      this.eventSigs.push([el, eventName, handler]);
      el.addEventListener(eventName, handler, false);
    },
    /**
     * Removes all event listeners added for this sticky.
     * @returns {void}
     */
    removeEvents: function removeEvents() {
      this.eventSigs.forEach(function (sig) {
        sig[0].removeEventListener(sig[1], sig[2]);
      });
      this.eventSigs = [];
    },
    /**
     * Add an observer to each `.spa-sticky` to watch for DOM node removal.
     * @returns {void}
     */
    setUpObservers: function setUpObservers() {
      var _this4 = this;
      if (window.MutationObserver) {
        // Observer for event cleanup.
        this.stickyEls.forEach(function (el) {
          var observer = new MutationObserver(_this4.handleObserve.bind(_this4));
          observer.observe(el.parentElement, {
            childList: true
          });
        });
      }
    },
    /**
     * If a `.spa-sticky` node has been removed from the DOM, remove it from the
     * `stickyElsInstances` list so events don't try to call methods on the removed instance.
     * If there are no more instances on the page, remove all event listeners.
     * @param {object} record - The `MutationRecord` object to watch.
     * @returns {void}
     */
    handleObserve: function handleObserve(record) {
      var _this5 = this;
      record.forEach(function (r) {
        if (r.removedNodes.length) {
          // Loop through the removed nodes.
          var nodes = _toConsumableArray(r.removedNodes);
          nodes.forEach(function (node) {
            if (node.classList.contains(CLASSES.STICKY)) {
              _this5.removeStickyInstances([node]);
            }
          });
        }
      });

      // This code block breaks toggleability of sticky elements
      // If all sticky elements are removed then the event listeners are canceled
      //  which breaks sticky functionality if one is re-enabled.
      // if (!this.stickyElsInstances.length) {
      //   this.removeEvents();
      // }
    },
    /**
     * Handles the resize event. Loops through instances and fires their `resize` method, passing
     * the current Y position.
     * @returns {void}
     */
    resizeHandler: function resizeHandler() {
      // Cache the current window width.
      var newWidth = document.documentElement.clientWidth;

      // If the width is unchanged, do nothing.
      if (viewportWidth === newWidth) {
        return;
      }

      // Otherwise, update the cached width, and run the resize code.
      viewportWidth = newWidth;
      var dist = getCurrentDist();
      this.stickyElsInstances.forEach(function (instance) {
        instance.handleResize(dist);
      });
    },
    /**
     * Triggers the resize handler if a change in page height is detected.
     * @returns {void}
     */
    checkPageHeight: function checkPageHeight() {
      var pageHeight = getPageHeight();
      if (this.pageHeight === pageHeight) {
        return;
      }
      this.pageHeight = pageHeight;
      this.resizeHandler();
    },
    /**
     * Loops through `.spa-sticky` elements and adds new `SpartaStickyEl` with initial values.
     * @param {array} [elsToCreate] - The sticky elements to create.
     * @returns {void}
     */
    createStickyInstances: function createStickyInstances(elsToCreate) {
      var _this6 = this;
      var elsArray = elsToCreate || this.stickyEls || [];
      if (!Array.isArray(elsArray)) {
        elsArray = _toConsumableArray(elsArray);
      }
      elsArray.forEach(function (elArg) {
        var el = elArg.length ? elArg[0] : elArg;
        var _getStickPoints2 = getStickPoints(el),
          stickPoint = _getStickPoints2.stickPoint,
          unstickPoint = _getStickPoints2.unstickPoint;
        var level = el.dataset.stickyLevel;
        if (!level) {
          level = 1;
          el.setAttribute('data-sticky-level', level);
        }
        var parent = el.dataset.stickyParent;
        if (!parent) {
          parent = 'body';
          el.setAttribute('data-sticky-parent', parent);
        }

        // Add a class for lazy-loaded modules.
        el.classList.add(CLASSES.STICKY);

        // Flag this sticky as initialized.
        el.setAttribute('data-initialized', 'true');

        // Make sticky instances.
        _this6.stickyElsInstances.push(new SpartaStickyEl(el, level, stickPoint, unstickPoint));

        // If the page is loaded scrolled, update the sticky elements.
        if (getCurrentDist() > 0) {
          _this6.updateStickyInstances();
        }
      }, this);

      // Once new elements have been added, update all the stickies as if a resize occurred.
      if (typeof elsToCreate !== 'undefined') {
        var dist = getCurrentDist();
        this.stickyElsInstances.forEach(function (instance) {
          instance.handleResize(dist);
        });
      }
    },
    /**
     * Loops through instances and removes sticky classes and styles.
     * @param {array} [elsToRemove] - The sticky elements to remove.
     * @returns {void}
     */
    removeStickyInstances: function removeStickyInstances(elsToRemove) {
      var _this7 = this;
      var elsArray = elsToRemove || this.stickyEls;
      if (!Array.isArray(elsArray)) {
        elsArray = _toConsumableArray(elsArray);
      }
      elsArray.forEach(function (elArg) {
        var el = elArg.length ? elArg[0] : elArg;

        // Loop through sticky instances and remove the instance that matches the removed node.
        var indexFound = null;
        _this7.stickyElsInstances.some(function (instance, i) {
          // TODO: Add `findIndex` polyfill.
          if (instance.el.isEqualNode(el)) {
            indexFound = i;
            return true;
          }
          return false;
        });
        if (indexFound !== null) {
          _this7.stickyElsInstances[indexFound].removePlaceholder();
          _this7.stickyElsInstances.splice(indexFound, 1);
          el.classList.remove(CLASSES.STICKY, CLASSES.ACTIVE, CLASSES.FADE_IN);
          var style = el.getAttribute('style');
          stickyElStyle(el, style);
          spartaEventsUtility.pub(EVENTS.ELEMENT_UNSTICK, _this7.el);
        }
      }, this);
    },
    /**
     * Loops through instances and calls their update method, passing the current Y position.
     * @returns {void}
     */
    updateStickyInstances: function updateStickyInstances() {
      if (!this.stickyElsInstances) {
        return;
      }
      var dist = getCurrentDist();
      this.stickyElsInstances.forEach(function (instance) {
        instance.handleResize(dist);
      });
    },
    /**
     * Removes the active state of the sticky.
     * @returns {void}
     */
    removeActiveState: function removeActiveState() {
      this.stickyEls.forEach(function (elArg) {
        var el = elArg.length ? elArg[0] : elArg;
        el.classList.remove(CLASSES.ACTIVE, CLASSES.FADE_IN);
        var style = el.getAttribute('style');
        stickyElStyle(el, style);
      });
    }
  };

  // Initialize all sticky elements as soon as Sparta is ready.
  spartaEventsUtility.sub(EVENTS.SPARTA_GLOBAL_READY, function () {
    if (els.length) {
      sticky.init(els);
    }
  });
  return {
    init: sticky.init.bind(sticky),
    addStickyEls: sticky.createStickyInstances.bind(sticky),
    removeStickyEls: sticky.removeStickyInstances.bind(sticky),
    updateStickyEls: sticky.updateStickyInstances.bind(sticky),
    recalcStickyEls: sticky.updateStickyInstances.bind(sticky),
    // Deprecated.
    EVENTS: EVENTS
  };
}(document.querySelectorAll(".".concat(CLASSES.STICKY)));
export { stickyApi as default };